import { CouponIcon, EditNoteIcon, EquipmentIcon, MixedChecklistIcon, ShapesIcon, UserIcon } from "@xola/icons";
import { PopoverList } from "@xola/ui-kit";
import { isEquipmentItem, isNonCanceledExperienceItem, now } from "common";
import React, { useState } from "react";
import {
    Event,
    ExperiencePurchaseItem,
    LineItem,
    PaymentMethod,
    Permission,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseStatus,
} from "types";
import { PastBookingWarningModal } from "../PurchasesMenu/PastBookingWarningModal";
import { PurchaseModificationModals } from "./PurchaseModificationModals";
import { queryClient } from "@/api/queryClient";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useModal } from "@/hooks/useVisibility";
import { getEnd } from "@/models/Event";
import {
    AFFILIATE_LINE_ITEM_CODES,
    getCodes,
    getCouponCodes,
    getLineItemsByPaymentMethods,
    getMembershipCodes,
} from "@/models/LineItem";
import { getGratuityEligibleItems } from "@/models/Purchase";
import { getTaxFeeLineItems, isMembershipItem } from "@/models/PurchaseItem";
import { CreatePurchaseItemState, CreatePurchaseState } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

interface ModifyPaymentPopoverProps {
    purchase: Purchase | CreatePurchaseState;
    item: PurchaseItem | CreatePurchaseItemState;
    isVisible: boolean;
    onClose: (id: string) => void;
}

type ModifyPaymentOption =
    | "codes"
    | "taxAndFees"
    | "guests"
    | "customLineItems"
    | "gratuity"
    | "fulfilledQuantity"
    | "merchandiseQuantity";

export const ModifyPaymentPopover = ({ purchase, item, isVisible, onClose }: ModifyPaymentPopoverProps) => {
    const [showModifyTaxFeesId, setShowModifyTaxFeesId] = useState([""]);
    const [showModifyGuestsId, setShowModifyGuestsId] = useState([""]);
    const [showModifyCustomItemsId, setShowModifyCustomItemsId] = useState([""]);
    const [showModifyGratuityId, setShowModifyGratuityId] = useState([""]);
    const [showModifyCodesId, setShowModifyCodesId] = useState([""]);
    const [showModifyFulfilledQuantityId, setShowModifyFulfilledQuantityId] = useState([""]);
    const [showUpdateMerchandiseQuantityId, setShowUpdateMerchandiseQuantityId] = useState("");
    const [showModifyChargesId, setShowModifyChargesId] = useState([""]);
    const itemId = item.id ?? "";
    const { isGranted } = useAuthorization();

    const handleModifyTaxFees = (id: string) => setShowModifyTaxFeesId(handleChange(id, showModifyTaxFeesId));
    const handleModifyGuests = (id: string) => setShowModifyGuestsId(handleChange(id, showModifyGuestsId));
    const handleModifyCodes = (id: string) => setShowModifyCodesId(handleChange(id, showModifyCodesId));
    const handleModifyCustomItems = (id: string) => {
        setShowModifyCustomItemsId(handleChange(id, showModifyCustomItemsId));
    };
    const handleModifyGratuity = (id: string) => {
        setShowModifyGratuityId(handleChange(id, showModifyGratuityId));
    };
    const handleModifyFulfilledQuantity = (id: string) => {
        setShowModifyFulfilledQuantityId(handleChange(id, showModifyFulfilledQuantityId));
    };
    const handleUpdateMerchandiseQuantity = (id: string) => {
        setShowUpdateMerchandiseQuantityId(showUpdateMerchandiseQuantityId ? "" : id);
    };

    const optionsStatus = getModifyPaymentOptionsStatus(purchase, item);
    const handleModifyCharges = (id: string) => setShowModifyChargesId(handleChange(id, showModifyChargesId));

    const handleItemClick = (callback: (id: string) => void) => {
        if (isPastEvent) {
            warningModal.show();
            setShowModificationModal(false);
        }

        onClose(itemId);
        callback(itemId);
    };

    const resetIds = () => {
        setShowModifyTaxFeesId([""]);
        setShowModifyGuestsId([""]);
        setShowModifyCustomItemsId([""]);
        setShowModifyCodesId([""]);
        setShowModifyChargesId([""]);
        setShowModifyGratuityId([""]);
        setShowModifyFulfilledQuantityId([""]);
        setShowUpdateMerchandiseQuantityId("");
    };

    const modalState = {
        taxes: { isOpen: showModifyTaxFeesId.includes(itemId), onChange: handleModifyTaxFees },
        guests: { isOpen: showModifyGuestsId.includes(itemId), onChange: handleModifyGuests },
        codes: { isOpen: showModifyCodesId.includes(itemId), onChange: handleModifyCodes },
        customItems: { isOpen: showModifyCustomItemsId.includes(itemId), onChange: handleModifyCustomItems },
        charges: { isOpen: showModifyChargesId.includes(itemId), onChange: handleModifyCharges },
        gratuity: { isOpen: showModifyGratuityId.includes(itemId), onChange: handleModifyGratuity },
        fulfilledQuantity: {
            isOpen: showModifyFulfilledQuantityId.includes(itemId),
            onChange: handleModifyFulfilledQuantity,
        },
        merchandiseQuantity: {
            isOpen: !!showUpdateMerchandiseQuantityId,
            lineItemId: showUpdateMerchandiseQuantityId,
            onChange: handleUpdateMerchandiseQuantity,
        },
    };

    const isPastEvent = (item ? [item] : purchase.items).some((item) => {
        if ("event" in item && item.event) {
            const event = queryClient.getQueryData<Event>(["events", item.event.id]);
            if (event) {
                const end = getEnd(event);
                return end.isBefore(now());
            }
        }

        return false;
    });
    const warningModal = useModal(false);
    const [showModificationModal, setShowModificationModal] = useState(true);
    const shouldShowGratuityOption =
        optionsStatus.gratuity &&
        isNonCanceledExperienceItem(item as ExperiencePurchaseItem) &&
        isGranted(Permission.RESEND_GRATUITY_EMAIL) &&
        item.seller?.id === getSeller().id;

    return (
        <>
            <PopoverList
                key={item.id}
                offset={[10, 80]}
                placement="left"
                className="w-50"
                visible={isVisible}
                onClickOutside={() => onClose(itemId)}
            >
                {optionsStatus.codes && item.seller?.id === getSeller().id && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyCodes)}>
                        <CouponIcon />
                        <span>Codes</span>
                    </PopoverList.Item>
                )}

                {optionsStatus.guests && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyGuests)}>
                        {isEquipmentItem(item as PurchaseItem) ? <EquipmentIcon /> : <UserIcon />}
                        <span>{isEquipmentItem(item as PurchaseItem) ? "Equipments" : "Guests"}</span>
                    </PopoverList.Item>
                )}
                {optionsStatus.merchandiseQuantity && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleUpdateMerchandiseQuantity)}>
                        <ShapesIcon />
                        <span>Change Quantity</span>
                    </PopoverList.Item>
                )}
                {optionsStatus.fulfilledQuantity && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyFulfilledQuantity)}>
                        <MixedChecklistIcon />
                        <span className="text-sm">Update Fulfilled Quantity</span>
                    </PopoverList.Item>
                )}

                {optionsStatus.taxAndFees && item.seller?.id === getSeller().id && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyTaxFees)}>
                        <EditNoteIcon />
                        <span>Taxes &amp; Fees</span>
                    </PopoverList.Item>
                )}

                {optionsStatus.customLineItems && item.seller?.id === getSeller().id && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyCustomItems)}>
                        <ShapesIcon />
                        <span>Custom Line Items</span>
                    </PopoverList.Item>
                )}

                {shouldShowGratuityOption && (
                    <PopoverList.Item name="list" onClickItem={() => handleItemClick(handleModifyGratuity)}>
                        <ShapesIcon />
                        <span>Gratuity</span>
                    </PopoverList.Item>
                )}
            </PopoverList>

            <PastBookingWarningModal
                isOpen={warningModal.isVisible}
                onClose={() => {
                    warningModal.hide();
                    resetIds(); // to reset modal state and prevents the modal from reopening on navigating between options after closing
                }}
                onContinue={() => {
                    warningModal.hide();
                    setShowModificationModal(true);
                }}
            />
            {showModificationModal && (
                <PurchaseModificationModals purchase={purchase} item={item} modalState={modalState} />
            )}
        </>
    );
};

const handleChange = (id: string, itemIDs: string[]) => {
    return itemIDs.includes(id) ? itemIDs.filter((popoverId) => popoverId !== id) : [...itemIDs, id];
};

const getModifyPaymentOptionsStatus = (
    purchase: Purchase | CreatePurchaseState,
    item: PurchaseItem | CreatePurchaseItemState,
): Record<ModifyPaymentOption, boolean> => {
    const isDraft = purchase.status === PurchaseStatus.DRAFT;
    const isMembership = isMembershipItem(item);
    const isMerchandise = item.object === PurchaseItemObject.WARE;
    const taxFeesLineItems = getTaxFeeLineItems(item);

    /** These functions would be called only for experience, membership and merchandise purchase, so we are not comparing other product types here */
    const isCommitedExperiencePurchase = !isDraft && !isMembership && !isMerchandise;
    const isCommitedMerchandisePurchase = !isDraft && isMerchandise;

    return {
        codes: getEditableCodeLineItems(purchase, item).length > 0,
        taxAndFees: taxFeesLineItems && taxFeesLineItems.length > 0 && !isMembership,
        guests: isCommitedExperiencePurchase,
        customLineItems: isCommitedExperiencePurchase || isCommitedMerchandisePurchase,
        gratuity:
            isCommitedExperiencePurchase &&
            getGratuityEligibleItems(purchase as Purchase).includes(item as ExperiencePurchaseItem),
        fulfilledQuantity: isCommitedMerchandisePurchase,
        merchandiseQuantity: isCommitedMerchandisePurchase,
    };
};

export const hasModifyPaymentOptions = (
    purchase: Purchase | CreatePurchaseState,
    item: PurchaseItem | CreatePurchaseItemState,
): boolean => {
    const optionsStatus = getModifyPaymentOptionsStatus(purchase, item);
    return Object.values(optionsStatus).some(Boolean);
};

export const getEditableCodeLineItems = (
    purchase: Purchase | CreatePurchaseState,
    item: PurchaseItem | CreatePurchaseItemState,
) => {
    return [
        ...getCodes(AFFILIATE_LINE_ITEM_CODES, item.lineItems as LineItem[]),
        ...getLineItemsByPaymentMethods([PaymentMethod.AFFILIATE_DEPOSIT], purchase.lineItems as LineItem[]),
        ...getCouponCodes(item.lineItems as LineItem[], purchase.lineItems as LineItem[]),
        ...getMembershipCodes(item.lineItems as LineItem[]),
        ...getLineItemsByPaymentMethods(
            [PaymentMethod.STORE_CREDIT, PaymentMethod.GIFT],
            purchase.lineItems as LineItem[],
        ),
    ];
};
